import { useEffect, useRef, useState } from "react";

/**
 * CountUp — animates the first integer found in `value` from 0 → target when
 * the element enters the viewport. Preserves any prefix/suffix around the
 * integer (so "24/7", "1:15", "5+", "100%", "8 lat" all animate naturally).
 *
 * - rAF-based, runs once per element (IO with `once`).
 * - Honours `prefers-reduced-motion: reduce` (renders final value instantly).
 */
export default function CountUp({
  value,
  duration = 1200,
  className = "",
  ...rest
}) {
  const ref = useRef(null);
  const [text, setText] = useState(() => String(value));

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const str = String(value);
    const match = str.match(/(\D*)(\d+)(.*)/);
    if (!match) {
      setText(str);
      return;
    }
    const [, prefix, intPart, suffix] = match;
    const target = parseInt(intPart, 10);

    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduce || typeof IntersectionObserver === "undefined") {
      setText(str);
      return;
    }

    let started = false;
    let raf = 0;

    const animate = () => {
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        // easeOutCubic
        const eased = 1 - Math.pow(1 - t, 3);
        const current = Math.round(target * eased);
        setText(`${prefix}${current}${suffix}`);
        if (t < 1) {
          raf = requestAnimationFrame(tick);
        } else {
          setText(str);
        }
      };
      raf = requestAnimationFrame(tick);
    };

    // Set initial state to 0 so it doesn't flash final value before IO fires.
    setText(`${prefix}0${suffix}`);

    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !started) {
            started = true;
            animate();
            obs.disconnect();
          }
        }
      },
      { threshold: 0.4 }
    );
    obs.observe(node);
    return () => {
      obs.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [value, duration]);

  return (
    <span ref={ref} className={`tabular-nums ${className}`} {...rest}>
      {text}
    </span>
  );
}
